import React from "react";
import Modal from "../../../../components/common/Modal";
import ContentToggle from "../../../../components/common/ContentToggle";

interface TestDataPageHelpModalProps {
  /** Controls whether the modal is visible */
  isOpen: boolean;
  /** Callback fired when the user closes the modal */
  onClose: () => void;
}

/**
 * TestDataPageHelpModal - Modal for displaying Test Data page Q&A help content.
 *
 * Shows FAQ sections about the Test Data tab using ContentToggle for
 * accordion-style collapsible sections.
 *
 * @param isOpen - Whether the modal is currently visible.
 * @param onClose - Callback to close the modal.
 * @returns The Test Data page help modal component.
 */
const TestDataPageHelpModal: React.FC<TestDataPageHelpModalProps> = ({
  isOpen,
  onClose,
}) => (
  <Modal
    isOpen={isOpen}
    title="Project Data Questions & Answers"
    onClose={onClose}
    onSecondary={onClose}
    clearBtn="Close"
    contentClassName="mb-4!"
  >
    <div className="flex flex-col gap-2 py-2">
      <ContentToggle titleText="What's new on the test data tab?" isFillOption>
        <p className="text-[14px]!">
          <span>UBT Tracker has help icons</span>
          <span>
            {" "}
            located on the right upper side of selected pages and next to most
            fields. These help icons offer instructions on how to fill out the
            field, view a project data sample, or refer to a LMP website link.
          </span>
        </p>
      </ContentToggle>

      <ContentToggle
        titleText="How do I record results from test of changes?"
        isFillOption
      >
        <ul className="list-disc pl-5 text-[14px]!">
          <li>
            <span>
              Record multiple data points to track your results and findings for
              each test of change. Before entering data in the Test Data Tab,
              you will need to have defined your SMART goal, metric and filled
              out other required fields in the{" "}
              <span className="underline">Project Details Tab.</span> In
              addition, you will need to have entered at least one record Test
              of Change Tab.
            </span>
            <br />
            <br />
            <img
              src="/assets/images/test-data-add-new.png"
              alt="UBT TOC Data Form"
              width={966}
              height={380}
              className="max-w-full"
            />
            <br />
            Click the <span className="font-semibold!">
              Add New Test Data
            </span>{" "}
            button,{" "}
            <span>
              a form will pop up allowing you to enter data on the metric you
              defined on the Project Details Tab. Below are the available
              fields:
            </span>
            <ul className="list-disc pl-5 mt-2">
              <li>
                Date of Test: Clicking on the box brings up a calendar, allowing
                you to select the date
              </li>
              <li>
                Shift: If your unit has different shifts and this is relevant to
                the test, you can choose the shift from a drop-down list
              </li>
              <li>
                Numerator: Number written above or to the left of the line in a
                common ratio or fraction to indicate the number of parts of the
                whole. For example, 2 is the numerator in the fraction 2/7. This
                field only accepts numerical values. Do not enter "-" (i.e.
                minus sign) or "%" (i.e. percentage sign) before or after the
                digits. The label at the top of the box is the same one you
                defined on the Project Details Tab
              </li>
              <li>
                Denominator: The label at the top of the box is the same one you
                defined on the Performance Improvement Project tab. This is the
                bottom number of your percentage
              </li>
              <li>
                Notes: If there are any notes you want to include about this
                particular observation (e.g., something unusual about the day or
                shift), enter them here
              </li>
              <li>
                Tracking Field: If you defined the Tracking Field on the
                Performance Improvement. Project screen, the label will show up
                here. If not, it will read Do Not Use.
              </li>
            </ul>
          </li>
        </ul>
      </ContentToggle>

      <ContentToggle
        titleText="How can I enter my team's baseline and target through the Test of Change Details Tab?"
        isFillOption
      >
        <p className="pl-5 text-[14px]!">
          You may establish your baseline and target by creating a new Test of
          Change (TOC). For example, you may create a TOC called
          &quot;Target&quot; and add Test Data{" "}
          <span className="underline">
            from the first day to the last day of the project.
          </span>{" "}
          Your graph results will display the test data from all other TOCs
          compared to your &quot;Target&quot; line from the beginning until the
          end of your project.
        </p>
      </ContentToggle>
      <ContentToggle
        titleText=" How can I display my baseline and target in the run chart (Test of Change Graph)?"
        isFillOption
      >
        <p className="pl-5 text-[14px]!">
          You can generate a Test Data Chart by selecting the Reports & Exports
          Button. This feature is especially useful when comparing project
          progress versus baseline or median.
        </p>
        <img
          src="/assets/images/test_data_chart_option.png"
          alt="Test data add new form example"
          className="w-full rounded"
        />
        <p className="pl-5 mt-3! text-[14px]!">
          First, you need to define your baseline and target by creating a new
          Test of Change (TOC) through the Test Of Change Details Tab. For
          example, you may create a TOC called “Target” and add Test Data from
          the first day to the last day of the project. Your graph results will
          display the test data from all other TOCs compared to your “Target”
          TOC.
        </p>
        <img
          src="/assets/images/toc-chart.png"
          alt="Test data add new form example"
          className="w-full rounded"
        />
      </ContentToggle>
      <ContentToggle
        titleText="How can I track a second metric in the same project and run chart?"
        isFillOption
      >
        <p className="pl-5 text-[14px]!">
          You can track a second metric by using the Tracking Field from the
          Test Of Change Tab. First you must label the additional metric that
          you’re trying to track. In the example below, the UBT is tracking the
          impact on Revenue after increasing the cash collection at point of
          service. The Department’s Revenue is the secondary metric. The team
          was working on increasing the cash collection as the primary goal;
          therefore, Cash Collection was the project metric.
        </p>
        <img
          src="/assets/images/track_project_run_chart.png"
          alt="Test data add new form example"
          className="w-full rounded"
        />
         <img
          src="/assets/images/toc_revenue_table.png"
          alt="Test data add new form example"
          className="w-full rounded"
        />
      </ContentToggle>
      <ContentToggle
        titleText=" How can I track the performance of multiple shifts or sub-teams in the same project and chart?"
        isFillOption
      >
        <p className="pl-5 text-[14px]!">
          You can track a second metric by using the Tracking Field from the
          Test Of Change Tab. First you must label the additional metric that
          you’re trying to track. In the example below, the UBT is tracking the
          impact on Revenue after increasing the cash collection at point of
          service. The Department’s Revenue is the secondary.
        </p>
        <img
          src="/assets/images/toc_by_shift.png"
          alt="Test data add new form example"
          className="w-full rounded"
        />
      </ContentToggle>
      <ContentToggle titleText="How can I print a run chart?" isFillOption>
        <p className="pl-5 text-[14px]!">
          You can print and display a run chart with the test of change data to
          your team members for brainstorming sessions or huddles with your
          team.
        </p>
        <p className="pl-5 mt-3! text-[14px]!">
          Select the Test Data Charts Options after clicking on the Reports &
          Exports Button. A new window opens with the chart. The Print Report
          Button is located on the top of the page.
        </p>
        <p className="pl-5 mt-3! text-[14px]!">
          You can also compare the impact of your project results to another
          metric. For example: You can run a run chart tracking employee
          attendance, and check the impact of your results on patient
          satisfaction through a bar chart in the UBT Tracker.
        </p>
        <p className="pl-5 mt-3! text-[14px]!">
          See Also <a href="https://www.lmpartnership.org/tools/ubt-tracker-user-guide" target="_blank" rel="noopener noreferrer">UBT Tracker User Guide</a>
        </p>
      </ContentToggle>
    </div>
  </Modal>
);

export default TestDataPageHelpModal;
